import {
  AUTH_API_HOSTNAME,
  AuthenticationProvider,
  useAuthentication,
} from '@autone/auth';
import { type AutoneTranslation } from '@autone/translations';
import { Toast } from '@autone/ui-next/atoms';
import { type Store } from '@reduxjs/toolkit';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import { createBrowserHistory } from 'history';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import React, {
  type ComponentProps,
  type FC,
  type PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider as ReduxProvider } from 'react-redux';
import { Router } from 'react-router-dom';

import { useAppSettings } from '../../hooks';
import { useGetUserConfigQuery } from '../../redux/services';
import { type ModuleName } from '../../types';
import { createLDMultiContext, getLDHash } from '../../utils/launchdarkly';
import { ErrorFallback } from '../../views/error';
import LoadingScreen from '../loading-screen/LoadingScreen';

import { AuthenticatedAppProviders } from './AuthenticatedAppProviders';

interface AppShellProps {
  appWrapper?: FC<PropsWithChildren<{ module: ModuleName }>>;
  reduxStore: Store;
  permissionGroups: string[];
  editPermissions?: string[] | null;
  translationNamespace: ComponentProps<typeof AutoneTranslation>['namespace'];
  permissions?: {
    admin: string[];
    read?: string[];
    edit: string[];
  };
  module: ModuleName;
  requiresAuthentication?: boolean;
  children: React.ReactNode;
}

const history = createBrowserHistory();

export const BaseAppWrapper: FC<PropsWithChildren<{ module: ModuleName }>> = ({
  module,
  children,
}) => {
  const [loading, setLoading] = useState(true);
  const ldClient = useLDClient();

  const { requiresAuthentication, user } = useAuthentication();

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    (async () => {
      await ldClient.waitUntilReady();
      const context = createLDMultiContext(user.customerId, user.userId);
      const hash = await getLDHash(context);
      await ldClient.identify(context, hash);
      setLoading(false);
    })();
  }, [loading, ldClient, user]);

  // set up app settings - intercom, sentry & fullview
  useAppSettings(module);

  // get the user config - this gets data from core/user/me and populates redux store
  useGetUserConfigQuery(undefined, { skip: !requiresAuthentication });

  if (loading) {
    return <LoadingScreen />;
  }

  return children;
};

export const AppShell: FC<{
  appWrapper?: FC<PropsWithChildren<{ module: ModuleName }>>;
  reduxStore: Store;
  permissionGroups: string[];
  editPermissions?: string[] | null;
  translationNamespace: ComponentProps<typeof AutoneTranslation>['namespace'];
  permissions?: {
    admin: string[];
    read?: string[];
    edit: string[];
  };
  module: ModuleName;
  requiresAuthentication?: boolean;
  children: React.ReactNode;
}> = ({
  appWrapper,
  reduxStore,
  permissionGroups,
  translationNamespace,
  /**
   * @Deprecated use 'permissions' instead
   */
  editPermissions = [],
  permissions,
  module,
  requiresAuthentication = true,
  children,
}: AppShellProps) => {
  useEffect(() => {
    window.addEventListener('vite:preloadError', (event) => {
      event.preventDefault();
      window.location.reload();
    });
  }, []);

  return (
    <Router history={history}>
      <ReduxProvider store={reduxStore}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <AuthKitProvider
            clientId={import.meta.env.VITE_WORKOS_CLIENT_ID!}
            apiHostname={AUTH_API_HOSTNAME}
            devMode={
              import.meta.env.MODE === 'development' ||
              import.meta.env.VITE_BUILD_STAGE === 'development'
            }
          >
            <AuthenticationProvider
              requiresAuthentication={requiresAuthentication}
            >
              <AuthenticatedAppProviders
                appWrapper={appWrapper ?? BaseAppWrapper}
                editPermissions={editPermissions}
                module={module}
                permissionGroups={permissionGroups}
                permissions={permissions}
                translationNamespace={translationNamespace}
              >
                {children}
                <Toast position="top-right" />
              </AuthenticatedAppProviders>
            </AuthenticationProvider>
          </AuthKitProvider>
        </ErrorBoundary>
      </ReduxProvider>
    </Router>
  );
};
